import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  input,
  Input,
  output,
  Output,
  signal,
} from '@angular/core';
import { SvgIconComponent } from '@ngneat/svg-icon';

import { AvatarsComponent, ConfirmDialogComponent, ConfirmDialogResult, HighlightDirective, Person } from '@libs/ui-lib';
import { TranslateModule } from '@ngx-translate/core';
import { Patient } from '@ffd/api-client';
import { MatMenuModule } from '@angular/material/menu';
import { Dialog } from '@angular/cdk/dialog';
import { DateEqualsPipe } from '../../pipes';

@Component({
  selector: 'lib-collaboration-list-item',
  templateUrl: './collaboration-list-item.component.html',
  styleUrl: './collaboration-list-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, SvgIconComponent, AvatarsComponent, HighlightDirective, DateEqualsPipe, TranslateModule, MatMenuModule],
})
export class CollaborationListItemComponent {
  public selected = signal(false);
  private readonly dialog = inject(Dialog);

  @HostBinding('class')
  public get isItemSelected(): string {
    return this.selected() ? 'group selected' : '';
  }

  @HostListener('click') public onHostClick(): void {
    this.openCollaboration.emit();
  }

  @Input() public linkedPatient!: Patient | undefined;
  @Input() public set isSelected(value: boolean) {
    this.selected.set(value);
  }
  @Input() public subject?: string;
  @Input() public participantsString!: string;
  @Input() public hasUnreadMessage: boolean = false;
  public participants = input<Person[]>([]);
  @Input() public lastMessage?: string;
  @Input() public lastMessageSenderFirstName?: string;
  @Input() public dateOfLastMessage?: Date;
  @Input() public searchTextToHighlight: string | null | undefined = null;
  public readonly daysLeftOfDeletion = input<number>();
  public readonly isCreatedBySelf = input<boolean>();
  public readonly isSupportCollab = input<boolean>();
  public readonly hasAttachmentInLastMessage = input<boolean>(false);
  public readonly isUserSiteAdmin = input<boolean>();
  @Output() public openCollaboration = new EventEmitter();
  public readonly leaveCollaboration = output<void>();
  public readonly recoverCollaboration = output<void>();
  public readonly deleteCollaboration = output<boolean>();

  public deleteCollabAvailable = computed(() => {
    const isDirectMessage = this.participants().length <= 1;

    return isDirectMessage || this.isCreatedBySelf() || this.isUserSiteAdmin();
  });

  public isGroupCollab = computed(() => {
    return this.participants().length >= 2;
  });

  public onMenuClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  public onLeaveCollabClick(): void {
    const dialogRef = this.dialog.open<ConfirmDialogResult>(ConfirmDialogComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light', 'collab-leave-backdrop'],
      panelClass: ['w-[360px]'],
      disableClose: true,
      data: {
        title: 'collab_leave_alert_title',
        description: 'collab_leave_alert_message',
        cancelLabel: 'collab_leave_alert_cancel',
        confirmLabel: 'collab_leave_alert_destructive',
        destructive: true,
      },
    });

    dialogRef.closed.subscribe((result) => {
      if (result?.confirm) {
        this.leaveCollaboration.emit();
      }
    });
  }

  public onRecoverCollabClick(): void {
    this.recoverCollaboration.emit();
  }

  public onDeleteCollabClick(): void {
    const dialogRef = this.dialog.open<ConfirmDialogResult>(ConfirmDialogComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light', 'collab-delete-backdrop'],
      panelClass: ['w-[360px]'],
      disableClose: true,
      data: {
        title: 'collab_delete_alert_title',
        description: 'collab_delete_alert_message',
        cancelLabel: 'collab_delete_alert_cancel',
        confirmLabel: 'collab_delete_alert_destructive',
        destructive: true,
      },
    });

    dialogRef.closed.subscribe((result) => {
      if (result?.confirm) {
        this.deleteCollaboration.emit(!!this.daysLeftOfDeletion());
      }
    });
  }
}
